import * as React from "react";
import { LabelValue } from "components/LabelValue/LabelValue";
import { ClientConfig, SystemSettings, SystemSettingsService } from "gen/clients";
import { useDialogState } from "hooks/useDialogState";
import { useQuery } from "react-query";
import { Stack } from "@mui/material";
import Button from "@mui/material/Button";
import { AssignAppBundleDialog } from "./AssignAppBundleDialog";

interface Props {
  clientConfig: ClientConfig;
  disabled?: boolean;
  onSaveSuccess: () => void;
}

const AppBundleField: React.FC<Props> = ({ clientConfig, disabled, onSaveSuccess }) => {
  const [isAssignDialogOpen, openAssignDialog, closeAssignDialog] = useDialogState();

  const { data: systemSettings } = useQuery<SystemSettings>(["getSystemSettings"], {
    queryFn: SystemSettingsService.getSystemSettings,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    cacheTime: 0
  });

  const appBundleName = React.useMemo(() => {
    if (!clientConfig.appBundleId || !systemSettings?.appBundles) {
      return "No app bundle assigned";
    }

    const appBundle = systemSettings.appBundles.find(bundle => bundle.id === clientConfig.appBundleId);

    return appBundle ? appBundle.name : "No app bundle assigned";
  }, [clientConfig.appBundleId, systemSettings]);

  const onAssignSuccess = React.useCallback(() => {
    onSaveSuccess();
    closeAssignDialog();
  }, [onSaveSuccess, closeAssignDialog]);

  return (
    <Stack spacing={1}>
      <LabelValue
        label="App Bundle"
        value={
          <Stack direction="row" spacing={1} alignItems="center">
            <div>{appBundleName}</div>
            <Button disabled={disabled} variant="outlined" size="small" onClick={openAssignDialog} sx={{ mt: 1 }}>
              Assign App Bundle
            </Button>
          </Stack>
        }
      />

      {isAssignDialogOpen && (
        <AssignAppBundleDialog clientConfig={clientConfig} onClose={closeAssignDialog} onSuccess={onAssignSuccess} />
      )}
    </Stack>
  );
};

export { AppBundleField };
